import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import {
  Alert,
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack
} from '@chakra-ui/react'
import { faClose } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SwitchFormControl } from 'BrokerTsLib/src/components'
import { NumberFormControl } from 'BrokerTsLib/src/components/forms/NumberFormControl'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { type ClientAdditionalInfoDto, type InsuranceParamsLeasingDto } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'
import { BadgeStep } from './ui/BadgeStep'

interface LeasingDriverInfoDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: ClientAdditionalInfoDto) => Promise<any>
  isSaving?: boolean
  initialValues?: ClientAdditionalInfoDto | null
  required?: boolean
}

export const LeasingDriverInfoDialog = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  initialValues,
  required,
}: LeasingDriverInfoDialogProps) => {
  const { translate } = useTranslationsContext()

  const methods = useForm<ClientAdditionalInfoDto>({
    defaultValues: {
      claims: initialValues?.claims ?? 0,
      driverYoungerThan24: initialValues?.driverYoungerThan24 ?? false,
    },
  })

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        claims: initialValues?.claims ?? 0,
        driverYoungerThan24: initialValues?.driverYoungerThan24 ?? false,
      })
    }
  }, [isOpen, initialValues])

  const { setError, formState: { errors } } = methods
  const handleSave = methods.handleSubmit(async (data) => {
    try {
      await onSave(data)
    }
    catch (e: any) {
      if (e?.response?.status === 400 && e?.response?.data) {
        const responseData = e.response.data
        if (responseData[''])
          setError('root', { type: 'manual', message: responseData[''] })
      }
    }
  })

  const { insuranceParams: insuranceParamsBase } = useInsuranceParams()
  const insuranceParams = insuranceParamsBase as InsuranceParamsLeasingDto

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={!required} closeOnEsc={!required} size={'lg'}>
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...methods}>
          <form onSubmit={handleSave}>
            <ModalHeader borderBottom={'1px'} borderColor={'gray.100'} as={HStack} gap={4} alignContent={'center'}>
              <BadgeStep>?</BadgeStep>
              <Box>{translate('leasingSimpleWizard.driverInfoDialog.title', 'Doplňující údaje')}</Box>

              {!required && <Button ml={'auto'} onClick={onClose} variant='ghost' size='sm' colorScheme='neutral' p={2}>
                <Icon as={FontAwesomeIcon} icon={faClose} size='xl'></Icon>
              </Button>}
            </ModalHeader>

            <ModalBody py={6} as={Stack} gap={6}>
              <NumberFormControl
                name="claims"
                label={translate('leasingSimpleWizard.driverInfoDialog.claims', 'Počet nehod klienta')}
                decimalPlaces={0}
                inputRightElement={null}
                showStepper
                w={'48'}
              />

              {insuranceParams?.client?.type === Selection.Person &&
                <SwitchFormControl
                  name="driverYoungerThan24"
                  label={translate('leasingSimpleWizard.driverInfoDialog.driverYounger24', 'Řidič mladší 24 let')}
                />
              }
            </ModalBody>

            <ModalFooter borderTop={'1px'} borderColor={'gray.100'}>
              <Stack gap={4} w='full'>
                <Button
                  colorScheme="green"
                  type="submit"
                  isLoading={isSaving}
                  ml={'auto'}
                >
                  {translate('save', 'Uložit')}
                </Button>
                {errors.root && <Alert status={'error'}>{errors.root.message}</Alert>}
              </Stack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}
